import type { SxProps, Theme } from '@mui/material';
import { getThemeSpacingCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';

// Shared gradient definition: fuchsia-500 → blue-500 → emerald-400 (seamless loop)
const AI_GRADIENT = 'linear-gradient(to right, #d946ef, #3b82f6, #34d399, #3b82f6, #d946ef)';
const NOTIFICATION_BELL_SIZE_PX = 32;
const NOTIFICATION_BELL_ICON_SIZE_PX = 16;

// One-directional right→left flow. Gradient ends match start (#d946ef both ends)
// so the repeat tile boundary is invisible — no flash, no direction reversal.
const gradientKeyframe = {
  '@keyframes gradientFlow': {
    '0%':   { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '200% 50%' },
  },
};

export const getBellButtonSx = (): SxProps<Theme> => theme => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[900],
  borderRadius: getCssPx(theme.shape.radius.xs),
  boxSizing: 'border-box',
  color: theme.palette.neutral[300],
  cursor: 'pointer',
  display: 'inline-flex',
  height: getCssPx(NOTIFICATION_BELL_SIZE_PX),
  justifyContent: 'center',
  minHeight: getCssPx(NOTIFICATION_BELL_SIZE_PX),
  minWidth: getCssPx(NOTIFICATION_BELL_SIZE_PX),
  padding: getThemeSpacingCssPx(theme, 'xs'),
  position: 'relative',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: getCssPx(NOTIFICATION_BELL_SIZE_PX),
  '& .MuiBadge-root': {
    alignItems: 'center',
    display: 'inline-flex',
    height: getCssPx(16),
    justifyContent: 'center',
    width: getCssPx(16),
  },
  '& .MuiBadge-badge': {
    borderRadius: getCssPx(theme.shape.radius.s),
    fontSize: getCssPx(6),
    fontWeight: theme.typography.fontWeightLight,
    height: getCssPx(10),
    lineHeight: getCssPx(6),
    minWidth: getCssPx(10),
    padding: 0,
    right: getCssPx(2),
    top: getCssPx(2),
    transform: 'none',
  },
  '& .MuiSvgIcon-root': {
    fill: 'currentColor',
    fontSize: getCssPx(NOTIFICATION_BELL_ICON_SIZE_PX),
    height: getCssPx(NOTIFICATION_BELL_ICON_SIZE_PX),
    width: getCssPx(NOTIFICATION_BELL_ICON_SIZE_PX),
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.brand[800],
    color: theme.palette.neutral[300],
  },
  '&[data-notification-in-progress="true"]': {
    boxShadow: `inset 0 0 0 1px ${theme.palette.brand[300]}`,
  },
});

export const panelPaperSx: SxProps<Theme> = {
  width: 400,
  maxHeight: '80vh',
  overflowY: 'auto',
  borderRadius: '16px',
  boxShadow: '0px 12px 40px rgba(0,0,0,0.14)',
  border: '1px solid #e8e8e8',
  p: 0,
};

export const panelHeaderSx: SxProps<Theme> = {
  px: 2.5,
  pt: 2.5,
  pb: 1.5,
  borderBottom: '1px solid #f0f0f0',
};

export const notificationsListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  px: 1.5,
  py: 1.5,
};

/** Panel list cards: always white; thin neutral edge + light shadow vs panel */
export const notificationPanelCardSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  backgroundColor: '#fff',
  border: '1px solid #e3e6ed',
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.055)',
  p: 1.5,
  overflow: 'hidden',
};

export const unreadStatusDotSx: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  alignSelf: 'center',
  mt: 0.25,
  backgroundColor: '#1877f2',
};

// STARTED: flat gray border — job is queued but not yet running
export const cardStartedSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  border: '2px solid #d1d5db',
  backgroundColor: '#fafafa',
  p: 1.5,
  overflow: 'hidden',
};

// PROCESSING: flowing gradient border — actively being worked on
export const cardInProgressSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '14px',
  p: '2px',
  background: AI_GRADIENT,
  backgroundSize: '200% 100%',
  animation: 'gradientFlow 3s linear infinite',
  ...gradientKeyframe,
};

// In-progress inner content box (white background masks the center)
export const cardInProgressInnerSx: SxProps<Theme> = {
  position: 'relative',
  zIndex: 1,
  background: '#fff',
  borderRadius: '12px',
  p: 1.5,
};

// Completed card: green border + subtle green tint
export const cardCompletedRecentSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  border: '2px solid #16a34a',
  background: '#f0fdf4',
  p: 1.5,
  overflow: 'hidden',
};

// Completed (older) card: muted green border
export const cardCompletedOlderSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  border: '2px solid #86efac',
  p: 1.5,
  overflow: 'hidden',
};

// Failed card: red border, white background
export const cardFailedSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  border: '2px solid #dc2626',
  background: '#fff',
  p: 1.5,
  overflow: 'hidden',
};

// Failed card read state: neutral border, white background (only border-color changes)
export const cardFailedReadSx: SxProps<Theme> = {
  position: 'relative',
  borderRadius: '12px',
  border: '2px solid #e3e6ed',
  background: '#fff',
  p: 1.5,
  overflow: 'hidden',
};

const iconCircleBase: SxProps<Theme> = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// in_progress / started
export const iconCircleSx: SxProps<Theme>           = { ...iconCircleBase, backgroundColor: '#f3f0ff' };
// completed
export const iconCircleCompletedSx: SxProps<Theme>  = { ...iconCircleBase, backgroundColor: '#dcfce7' };
// failed
export const iconCircleFailedSx: SxProps<Theme>     = { ...iconCircleBase, backgroundColor: '#fee2e2' };

export const cardTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: '0.95rem',
  flex: 1,
  color: '#111',
};

export const timestampSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};

/** Compact icon circle for themed (non-job) notifications */
export const getThemedIconCircleSx = (iconBg: string): SxProps<Theme> => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: iconBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

export const notificationSubtitleSx: SxProps<Theme> = {
  fontSize: '0.83rem',
  color: '#374151',
  mt: 0.5,
  lineHeight: 1.45,
};

export const jobCompanyNameSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.85rem',
  color: 'text.secondary',
  mt: 0.5,
  lineHeight: 1.2,
};

/** Outlined CTA chip used for external/internal links on themed notifications */
export const getNotificationLinkChipSx = (colors: {
  titleColor: string;
  border: string;
}): SxProps<Theme> => ({
  fontSize: '0.78rem',
  fontWeight: 600,
  color: colors.titleColor,
  border: `1px solid ${colors.border}`,
  borderRadius: '6px',
  px: 1.25,
  py: 0.375,
  textDecoration: 'none',
  display: 'inline-block',
  lineHeight: 1.6,
  '&:hover': { opacity: 0.7 },
});

export const themedCardSurfaceSx = (theme: {
  bg: string;
  border: string;
}): SxProps<Theme> => ({
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  borderRadius: '12px',
  p: 1.5,
  cursor: 'pointer',
});

export const themedToastSurfaceSx = (theme: {
  bg: string;
  border: string;
}): SxProps<Theme> => ({
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  borderRadius: '14px',
  p: 1.5,
  width: 360,
  boxShadow: '0px 8px 32px rgba(0,0,0,0.13)',
  cursor: 'default',
});

export const notificationRowGridSx = (showUnreadColumn: boolean): SxProps<Theme> => ({
  display: 'grid',
  gridTemplateColumns: showUnreadColumn ? 'auto 1fr auto auto' : 'auto 1fr auto',
  gap: 1,
  alignItems: 'flex-start',
});

export const unreadColumnSx: SxProps<Theme> = {
  width: 10,
  display: 'flex',
  justifyContent: 'center',
  alignSelf: 'flex-start',
  pt: 0.625,
  flexShrink: 0,
};

// "Processing" badge: flowing gradient — job is actively running
export const inProgressBadgeSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  mt: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: '12px',
  background: AI_GRADIENT,
  backgroundSize: '200% 100%',
  animation: 'gradientFlow 3s linear infinite',
  ...gradientKeyframe,
  color: '#fff',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
};

// "Starting up" badge: static purple — job queued but not yet running
export const startedBadgeSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  mt: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: '12px',
  background: '#ede9fe',
  color: '#5b21b6',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
};

export const descriptionSx: SxProps<Theme> = {
  fontSize: '0.83rem',
  color: 'text.secondary',
  lineHeight: 1.45,
  mb: 0.75,
};

export const tagsRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 0.75,
};

// PROCESSING state chip — purple
export const processingTypeChipSx: SxProps<Theme> = {
  fontSize: '0.72rem',
  height: 22,
  backgroundColor: '#ede9fe',
  color: '#5b21b6',
  fontWeight: 600,
  borderRadius: '6px',
};

// STARTED state chip — gray (matches flat gray border)
export const startedTypeChipSx: SxProps<Theme> = {
  fontSize: '0.72rem',
  height: 22,
  backgroundColor: '#f3f4f6',
  color: '#4b5563',
  fontWeight: 600,
  borderRadius: '6px',
};

export const preferencesSectionSx: SxProps<Theme> = {
  px: 2.5,
  pt: 1.5,
  pb: 1.5,
  borderBottom: '1px solid #f0f0f0',
};

export const preferencesHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
};

export const preferencesRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: 0.75,
};

export const preferencesSubSectionSx: SxProps<Theme> = {
  border: '1px solid #ececec',
  borderRadius: '8px',
  px: 1,
  py: 0.75,
  mb: 1,
};

export const preferenceScrollableListSx: SxProps<Theme> = {
  maxHeight: 126,
  overflowY: 'auto',
  pr: 0.5,
};

export const filesToggleSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#5b21b6',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  p: 0,
  mt: 0.5,
  '&:hover': { textDecoration: 'underline' },
};

export const fileItemSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  lineHeight: 1.5,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const viewResultsLinkSx: SxProps<Theme> = {
  fontSize: '0.83rem',
  fontWeight: 700,
  color: 'primary.main',
  cursor: 'pointer !important',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

// ── Preferences panel ────────────────────────────────────────────────────────

export const prefSectionSx: SxProps<Theme> = {
  px: 2.5,
  pt: 1.5,
  pb: 1.5,
  borderBottom: '1px solid #f0f0f0',
};

export const prefSubheaderSx: SxProps<Theme> = {
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mt: 1,
  mb: 0.5,
};

export const prefSubheaderRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const prefCheckboxSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  mx: 0,
  my: 0,
  '& .MuiFormControlLabel-label': { lineHeight: 1.4 },
};

export const companyListSx: SxProps<Theme> = {
  maxHeight: 148,
  overflowY: 'auto',
  pr: 0.5,
  mt: 0.25,
  border: '1px solid #ececec',
  borderRadius: '8px',
  px: 1,
  py: 0.5,
};

export const companyRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  mx: 0,
  my: 0,
  width: '100%',
  '& .MuiFormControlLabel-label': { overflow: 'hidden', lineHeight: 1.4 },
};
